import { useCallback } from 'react';
import { useTwilioWS } from '@hooks/twilioWS';
import useMyRole from '@hooks/useMyRole';
import { CallStatus, TwilioEventStatus } from '@hooks/useCallStatus';
import type { Appointment } from 'vl-common/src/schemas/shared';
import useAppointmentEvents from '@hooks/useAppointmentEvents';
import { useVideoCallSignallingType } from '@hooks/useVideoCallSignallingType';
import { useAppointmentRoomDT } from '@hooks/useTestPatientRoom';
import { Lobby, RingerStopReason } from '@lib/Lobby';
import { Modal } from 'antd';
import { Room } from 'twilio-video';
import useSendTwilioEvents from '@hooks/useSendTwilioEvents';

function stopLocalAVTracks(room: Room) {
  const localParticipant = room.localParticipant;
  const tracks = [
    ...Array.from(localParticipant.videoTracks.values()),
    ...Array.from(localParticipant.audioTracks.values())
  ];

  tracks.forEach((publication) => {
    const track = publication.track;

    track.stop();
    publication.unpublish();
  });
}

export function leaveRoom(room: Room) {
  if (room) {
    stopLocalAVTracks(room);
    room.disconnect();
  }
}

/**
 * Returns a pair of zero argument "action" functions that (i) record the call status in the backend and (ii)
 * issue WebSocket updates to keep the other party up-to-date. For use by the clinician only.
 */
function useCallStatusUpdatersWS(appointment: Appointment) {
  const { appointment_id, patient_user_guid } = appointment;
  const role = useMyRole();
  const declineEvent = role === 'clinician' ? 'CADECLINED' : 'USRDECLINED';
  const startEvent = role === 'clinician' ? 'CASTART' : 'START';
  const endEvent = role === 'clinician' ? 'CAEND' : 'END';
  const { sendControlMessage, open, close } = useTwilioWS(appointment);
  const { close: closeEventsWS, connected } = useAppointmentEvents(appointment);
  const sendTwilioEvents = useSendTwilioEvents();

  const sendTwilioEvent = useCallback(
    async (callStatus: CallStatus, event: TwilioEventStatus) => {
      if (open) {
        sendControlMessage({ callStatus, event });
        await sendTwilioEvents({
          userGuid: patient_user_guid,
          appointmentId: appointment_id,
          event
        });
      }
    },
    [appointment_id, close, closeEventsWS, sendTwilioEvents, open, patient_user_guid, sendControlMessage]
  );

  return {
    startCall: useCallback(() => sendTwilioEvent('in-progress', startEvent), [sendTwilioEvent, startEvent]),
    endCall: useCallback(() => sendTwilioEvent('completed', endEvent), [sendTwilioEvent, endEvent]),
    stopCalling: useCallback(() => sendTwilioEvent('not-started', declineEvent), [declineEvent, sendTwilioEvent]),
    dial: useCallback(() => sendTwilioEvent('calling', 'CACALLING'), [sendTwilioEvent]),
    decline: useCallback(() => sendTwilioEvent('not-started', declineEvent), [declineEvent, sendTwilioEvent]),
    appointmentTaken: useCallback(() => sendTwilioEvent('not-started', 'CACHANGED'), [sendTwilioEvent]),
    updatersReady: open && connected
  };
}

// NB must be global because a local ref doesn't protect different instances of this hook from racing
let isStartingCall = false;

function useCallStatusUpdatersDT(appointment: Appointment) {
  const { patient_user_guid, appointment_id } = appointment;
  const { room, signalingManager: protocol } = useAppointmentRoomDT(appointment);
  const sendTwilioEvents = useSendTwilioEvents();
  const now = new Date();

  return {
    // sent by the patient when they accept the offer to start from the clinician
    startCall: useCallback(async () => {
      if (isStartingCall) return;
      try {
        isStartingCall = true;

        if (!room) throw Error('room not initialized');
        const lobby = Lobby.forRoom(room);

        lobby.callStatus = 'in-progress';
        await lobby.join().catch((e) => {
          throw Error(`Error connecting to camera and/or microphone: ${e.message}`);
        });

        return sendTwilioEvents({
          userGuid: patient_user_guid,
          appointmentId: appointment_id,
          event: 'START'
        });
      } catch (e) {
        Modal.error({ title: 'Error starting call', content: e.message });
      } finally {
        isStartingCall = false;
      }
    }, [room]),

    // sent by the clinician when they decide to end the call
    endCall: useCallback(async () => {
      protocol?.terminateCall().then(undefined, (e) => console.error(e));
      await sendTwilioEvents({
        userGuid: patient_user_guid,
        appointmentId: appointment_id,
        event: 'CAEND'
      });
      if (room) leaveRoom(room);
    }, [protocol]),

    // sent by the clinician when they want to start a call
    dial: useCallback(() => {
      if (!protocol) throw Error('protocol not initialized');

      return protocol.offerToStart();
    }, [room, protocol]),

    // sent by the clinician when they cancel a "dial"
    stopCalling: useCallback((reason: RingerStopReason = 'cancelled') => protocol?.cancelCall(reason), [protocol]),

    // sent by the patient when they decline the call offer
    decline: useCallback(() => protocol?.declineCall(), [protocol]),

    // todo - no idea what this is???
    appointmentTaken: useCallback(() => {
      throw Error('appointmentTaken call status updater not implemented for DT');
    }, []),
    updatersReady: !!room,
    now
  };
}

/* eslint-disable react-hooks/rules-of-hooks */
// Returns the call status of the given appointment, which *must* be stable. Switches between implementations.
export default function useCallStatusUpdaters(appointment: Appointment) {
  if (useVideoCallSignallingType(appointment) === 'ws') {
    return useCallStatusUpdatersWS(appointment);
  }

  return useCallStatusUpdatersDT(appointment);
}
